import {
  Field,
  Text,
  ImageField,
  LinkField,
  useSitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import ImageWrapper from 'helpers/ImageWrapper/ImageWrapper';
import LinkWrapper from 'helpers/LinkWrapper/LinkWrapper';
import RichTextA11yWrapper from 'helpers/RichTextA11yWrapper/RichTextA11yWrapper';
import React from 'react';
import { useDictionary } from 'lib/hooks/use-dictionary';

type ExpertiseProps = {
  fields: {
    Expertise: Field<string>;
  };
};

export type ExportDetailsProps = {
  ExpertImage: ImageField;
  ExpertFirstName: Field<string>;
  ExpertLastName: Field<string>;
  ExpertSubtitle: Field<string>;
  CompanyName: { fields: { CompanyName: Field<string> } };
  Expertise: Array<ExpertiseProps>;
  ExpertBio: Field<string>;
  ExpertLink: LinkField;
};

const ExpertDetailsCard = () => {
  const { sitecoreContext } = useSitecoreContext();
  const dictionary = useDictionary();

  const {
    ExpertImage,
    ExpertFirstName,
    ExpertLastName,
    ExpertSubtitle,
    Expertise,
    CompanyName,
    ExpertBio,
    ExpertLink,
  } = sitecoreContext?.route?.fields as unknown as ExportDetailsProps;

  const FullName = ExpertFirstName ? `${ExpertFirstName?.value} ${ExpertLastName?.value}` : '';

  return (
    <section className="component container spacing-sm">
      <div className="p-4 md:p-8 bg-light-background-neutral-variant border border-light-stroke-primary rounded-lg flex flex-col md:flex-row gap-8">
        {ExpertImage?.value?.src && (
          <div className="max-w-full md:min-w-[180px] md:max-w-[180px] lg:min-w-[327px] lg:max-w-[327px]">
            <ImageWrapper
              field={ExpertImage}
              height="327"
              width="409"
              className={'aspect-[327/409] object-cover w-full'}
            />
          </div>
        )}

        <div
          className={classNames(
            'flex flex-col gap-6 w-full',
            ExpertImage ? 'lg:w-[calc(100%-327px)]' : ''
          )}
        >
          <div className="relative ps-4 before:absolute before:bg-light-background-secondary-brand before:left-0 before:top-0 before:h-full before:w-0.5">
            <Text tag="h3" field={{ value: FullName }} />

            <Text tag="p" field={ExpertSubtitle} className="p2 text-light-fill-primary-variant" />

            <Text
              tag="p"
              field={CompanyName?.fields?.CompanyName}
              className="p3 text-light-fill-primary-variant"
            />
          </div>
          <div>
            <p className="c1-medium mb-2">
              {dictionary?.['Experts.Expertise.Label'] || 'Expertise'}
            </p>
            {Expertise && Expertise.length > 0 && (
              <ul className="flex flex-wrap sm:flex-nowrap gap-x-10 gap-y-2">
                {Expertise?.slice(0, 3)?.map((item: ExpertiseProps, index: number) => (
                  <li key={index} className="grow">
                    <Text
                      field={item?.fields?.Expertise}
                      tag="span"
                      className="p1 text-light-fill-primary-variant"
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>

          <hr className="solid border-light-stroke-primary my-0" />

          <RichTextA11yWrapper className="p2 rtaw" field={ExpertBio} />
          <div>
            <LinkWrapper className="p2-medium link" field={ExpertLink} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExpertDetailsCard;
